import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Network, Search, BarChart3, Users, ChevronDown, ChevronRight, Crown } from 'lucide-react';

// Cycle of border/badge colors per depth level, purely for visual variety —
// same idea as the reference org-chart mock (each generation gets its own color).
const LEVEL_COLORS = ['#22c55e', 'var(--primary)', '#a855f7', '#ef4444', '#3b82f6', '#ec4899'];

function formatCurrency(n) {
  return `₹${(n || 0).toLocaleString('en-IN')}`;
}

// One node in the org chart, rendered recursively as a nested <ul><li> tree.
// The connecting lines are pure CSS (see .org-tree rules in index.css) — no
// canvas/SVG needed, so it degrades gracefully and stays selectable/searchable text.
function TreeNode({ node, depth, expanded, toggleExpand, highlightId }) {
  const hasChildren = node.children && node.children.length > 0;
  const isOpen = expanded.has(node._id);
  const color = node.role === 'admin' ? '#22c55e' : LEVEL_COLORS[depth % LEVEL_COLORS.length];
  const isHighlighted = highlightId === node._id;
  const showChildren = hasChildren && isOpen;

  return (
    <div className="ot-branch">
      <div
        className={`org-node ${isHighlighted ? 'org-node-highlight' : ''}`}
        style={{ borderColor: isHighlighted ? 'var(--primary)' : `${color}55` }}
      >
        <div className="org-node-avatar" style={{ background: `${color}22`, color, borderColor: color }}>
          {node.role === 'admin' ? <Crown size={16} /> : (node.name?.[0] || '?').toUpperCase()}
        </div>
        <div className="org-node-name">{node.name}</div>
        <div className="org-node-code">{node.referralCode}</div>
        {node.isBlocked && <span className="badge badge-danger" style={{ fontSize: '0.6rem', padding: '2px 6px' }}>Blocked</span>}
        <div className="org-node-row">
          <span className={`badge ${node.isActive ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.62rem', padding: '2px 8px' }}>
            {node.isActive ? 'Active' : 'Inactive'}
          </span>
          <span style={{ fontSize: '0.68rem', color: 'var(--primary)', fontWeight: 700 }}>
            {node.activePackage > 0 ? formatCurrency(node.activePackage) : '—'}
          </span>
        </div>
        <div className="org-node-stats">
          <span title="Team size">Team: {node.totalTeam || 0}</span>
          <span title="Team business">{formatCurrency(node.totalBusiness)}</span>
        </div>

        {hasChildren && (
          <button
            type="button"
            className="org-node-toggle"
            onClick={() => toggleExpand(node._id)}
            title={isOpen ? 'Collapse' : `Expand (${node.children.length})`}
          >
            {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {node.children.length}
          </button>
        )}
      </div>

      {showChildren && (
        <>
          <div className="ot-vline" />
          <div className="ot-children-row">
            {node.children.map((child, idx) => (
              <div
                key={child._id}
                className={`ot-child-col ${idx === 0 ? 'ot-first' : ''} ${idx === node.children.length - 1 ? 'ot-last' : ''}`}
              >
                <div className="ot-child-col-stem" />
                <TreeNode node={child} depth={depth + 1} expanded={expanded} toggleExpand={toggleExpand} highlightId={highlightId} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Collects the id of every node down to (and including) `depth` levels —
// used to auto-expand the first couple of generations by default so the
// page isn't a single collapsed dot on load.
function collectIdsUpToDepth(nodes, depth, acc) {
  if (depth < 0) return acc;
  nodes.forEach(n => {
    acc.add(n._id);
    if (n.children?.length) collectIdsUpToDepth(n.children, depth - 1, acc);
  });
  return acc;
}

// Finds the chain of ancestor ids leading down to a node matching `query`
// (by name or referral code) — used so search can auto-expand + highlight it.
function findMatchPath(nodes, query, path = []) {
  for (const n of nodes) {
    const nextPath = [...path, n._id];
    if (
      n.name?.toLowerCase().includes(query) ||
      n.referralCode?.toLowerCase().includes(query)
    ) {
      return nextPath;
    }
    if (n.children?.length) {
      const found = findMatchPath(n.children, query, nextPath);
      if (found) return found;
    }
  }
  return null;
}

export default function AdminTeam() {
  const [tab, setTab] = useState('tree');

  // ===================== Full Team Tree (all users, always visible) =====================
  const [treeLoading, setTreeLoading] = useState(true);
  const [roots, setRoots] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');
  const [highlightId, setHighlightId] = useState(null);

  const fetchFullTree = useCallback(() => {
    setTreeLoading(true);
    api.get('/users/admin/team-tree-full')
      .then(r => {
        setRoots(r.data.roots);
        setTotalUsers(r.data.totalUsers);
        // Auto-expand root + first 2 generations so there's something useful
        // to look at immediately, without dumping the entire (possibly huge) tree open.
        setExpanded(collectIdsUpToDepth(r.data.roots, 2, new Set()));
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load team tree'))
      .finally(() => setTreeLoading(false));
  }, []);

  useEffect(() => { fetchFullTree(); }, [fetchFullTree]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const all = new Set();
    const walk = (nodes) => nodes.forEach(n => { all.add(n._id); if (n.children?.length) walk(n.children); });
    walk(roots);
    setExpanded(all);
  };

  const collapseAll = () => setExpanded(collectIdsUpToDepth(roots, 0, new Set()));

  const handleSearch = (e) => {
    e.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) { setHighlightId(null); return; }
    const path = findMatchPath(roots, q);
    if (!path) { toast.error('No matching user in the tree'); setHighlightId(null); return; }
    setExpanded(prev => new Set([...prev, ...path]));
    setHighlightId(path[path.length - 1]);
    setTimeout(() => {
      document.querySelector('.org-node-highlight')?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }, 50);
  };

  // ===================== Business Report tab (unchanged) =====================
  const [users, setUsers] = useState([]);
  const [platformTotals, setPlatformTotals] = useState({ totalBusiness: 0, totalTeam: 0 });
  const [total, setTotal] = useState(0);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportSearch, setReportSearch] = useState('');
  const [sortBy, setSortBy] = useState('totalBusiness');
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  const fetchReport = useCallback(() => {
    setReportLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT, sortBy });
    if (reportSearch) params.append('search', reportSearch);
    api.get(`/users/admin/business-report?${params}`)
      .then(r => { setUsers(r.data.users); setTotal(r.data.total); setPlatformTotals(r.data.platformTotals); })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load business report'))
      .finally(() => setReportLoading(false));
  }, [page, sortBy, reportSearch]);

  useEffect(() => { if (tab === 'report') fetchReport(); }, [tab, fetchReport]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <DashboardLayout>
      <h1 className="page-title">Team Management</h1>
      <p className="page-subtitle">Every user on the platform, laid out by sponsor — or the business leaderboard</p>

      <div className="tab-bar" style={{ marginBottom: 16 }}>
        <button className={`tab-btn ${tab === 'tree' ? 'active' : ''}`} onClick={() => setTab('tree')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Network size={14} /> Team Tree
        </button>
        <button className={`tab-btn ${tab === 'report' ? 'active' : ''}`} onClick={() => setTab('report')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <BarChart3 size={14} /> Business Report
        </button>
      </div>

      {tab === 'tree' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Search size={14} /> Jump to a user (name or referral code)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Ram or TP12345"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-sm">Find</button>
              
            </form>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Users size={18} /> All Users</h3>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{totalUsers} total users</div>
            </div>

            {treeLoading ? (
              <div className="loader-wrap"><div className="loader"></div></div>
            ) : roots.length === 0 ? (
              <div className="empty-state"><div className="empty-icon"><Users size={40} /></div><h3>No users yet</h3></div>
            ) : (
              <div className="org-tree-wrap">
                <div className="ot-roots-row">
                  {roots.map(r => (
                    <TreeNode key={r._id} node={r} depth={0} expanded={expanded} toggleExpand={toggleExpand} highlightId={highlightId} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'report' && (
        <>
          <div className="wallet-grid" style={{ marginBottom: 24 }}>
            <div className="wallet-card">
              <div className="wallet-label">Platform Total Business</div>
              <div className="wallet-value" style={{ color: 'var(--primary)' }}>{formatCurrency(platformTotals.totalBusiness)}</div>
            </div>
            <div className="wallet-card">
              <div className="wallet-label">Total Direct Referrals (all users)</div>
              <div className="wallet-value" style={{ color: '#3b82f6' }}>{platformTotals.totalTeam}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><BarChart3 size={18} /> Business Leaderboard</h3>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Total: {total} users</div>
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
              <div className="form-group" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Search size={14} /> Search</label>
                <input type="text" className="form-control" placeholder="Name, email, or referral code..."
                  value={reportSearch} onChange={e => { setReportSearch(e.target.value); setPage(1); }} />
              </div>
              <div className="form-group" style={{ minWidth: 200, marginBottom: 0 }}>
                <label>Sort By</label>
                <select className="form-control" value={sortBy} onChange={e => { setSortBy(e.target.value); setPage(1); }}>
                  <option value="totalBusiness">Total Business</option>
                  <option value="totalTeam">Total Team Size</option>
                  <option value="totalDirects">Direct Referrals</option>
                  <option value="totalEarned">Total Earned</option>
                </select>
              </div>
            </div>

            {reportLoading ? (
              <div className="loader-wrap"><div className="loader"></div></div>
            ) : users.length === 0 ? (
              <div className="empty-state"><div className="empty-icon"><Users size={40} /></div><h3>No users found</h3></div>
            ) : (
              <div className="table-wrap">
                <table className="tp-table">
                  <thead><tr><th>#</th><th>Name</th><th>Referral Code</th><th>Directs</th><th>Team Size</th><th>Total Business</th><th>Total Earned</th></tr></thead>
                  <tbody>
                    {users.map((u, i) => (
                      <tr key={u._id}>
                        <td>{(page - 1) * LIMIT + i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{u.name}</td>
                        <td>{u.referralCode}</td>
                        <td>{u.totalDirects}</td>
                        <td>{u.totalTeam}</td>
                        <td style={{ color: 'var(--primary)', fontWeight: 700 }}>{formatCurrency(u.totalBusiness)}</td>
                        <td>{formatCurrency(u.totalEarned)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const p = i + Math.max(1, page - 2);
                  if (p > totalPages) return null;
                  return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
                })}
                <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
              </div>
            )}
          </div>
        </>
      )}
    </DashboardLayout>
  );
}